import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { useEffect } from 'react';
import Button from '@alltrails/shared/denali/components/Button';
import logExploreWithProButtonClicked from '@alltrails/analytics/events/logExploreWithProButtonClicked';
import logProWelcomePageViewed from '@alltrails/analytics/events/logProWelcomePageViewed';
import CustomProvider from 'components/CustomProvider';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import Typography from 'components/Typography';
import ApplicationLayout from 'components/ApplicationLayout';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import { Context } from '@alltrails/shared/types/context';
import * as styles from './ProWelcome.module.scss';
import hero768wAvifSrc from './hero-768w.avif';
import hero768wWebpSrc from './hero-768w.webp';
import hero768wJpgSrc from './hero-768w.jpg';
import hero2800wJpg from './hero-2800w.jpg';
import hero2800wAvif from './hero-2800w.avif';
import hero2800wWebp from './hero-2800w.webp';

const br = () => <br />;

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16796
function ProWelcome() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    logProWelcomePageViewed();
  }, []);

  const buttonDestination = wrapUrlSafe(user ? '/explore' : '/login', languageRegionCode);

  return (
    <CustomProvider>
      <div className={styles.content}>
        <picture>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={hero768wAvifSrc} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={hero768wWebpSrc} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={hero768wJpgSrc} type="image/jpg" />
          <source srcSet={hero2800wAvif} type="image/avif" />
          <source srcSet={hero2800wWebp} type="image/webp" />
          <img
            className={styles.img}
            src={hero2800wJpg}
            alt={intl.formatMessage({ defaultMessage: 'On a beautiful, clear day, a solo mountain biker heads off into a rocky desert landscape.' })}
          />
        </picture>
        <div className={styles.welcome}>
          <Typography component="h1" variant="heading700">
            <FormattedMessage defaultMessage="Welcome to AllTrails+" />
          </Typography>
          <Typography variant="paragraph300">
            <FormattedMessage
              defaultMessage="You can now do even more outdoors with extra safety and planning<br></br> features like offline maps, 3D views, and wrong-turn alerts."
              values={{ br }}
            />
          </Typography>
          <div>
            <Button
              text={<FormattedMessage defaultMessage="Start exploring" />}
              linkInfo={{ href: buttonDestination }}
              onClick={() => logExploreWithProButtonClicked()}
              testId="pro-welcome-start-exploring"
              variant="accent"
            />
          </div>
        </div>
      </div>
    </CustomProvider>
  );
}

type Props = {
  context: Context;
  currentPage: string;
  locationSlug: string;
};

export default function Wrapper({ context, currentPage, locationSlug }: Props) {
  return (
    <ApplicationLayout context={context} currentPage={currentPage} locationSlug={locationSlug}>
      <CustomProvider>
        <ProWelcome />
      </CustomProvider>
    </ApplicationLayout>
  );
}
